import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { Bell, Calendar, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const AnnouncementsPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const res = await api.get('/announcements/active');
            setAnnouncements(res.data.announcements || []);
        } catch (error) {
            console.error('Failed to load announcements:', error);
            // Fallback dummy data if API fails (for demo/dev)
            setAnnouncements([
                {
                    id: 1,
                    title: 'System Maintenance Scheduled for Feb 15',
                    content: 'We will be performing scheduled maintenance on our trading engine. Services may be interrupted for up to 30 minutes.',
                    type: 'warning',
                    createdAt: new Date().toISOString()
                },
                {
                    id: 2,
                    title: 'New Token Listing: SOL and DOT now available!',
                    content: 'We are excited to announce the listing of Solana (SOL) and Polkadot (DOT) on Albatech. Happy trading!',
                    type: 'success',
                    createdAt: new Date(Date.now() - 86400000).toISOString()
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getBadgeColor = (type) => {
        switch (type) {
            case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'success': return 'bg-green-500/20 text-green-400 border-green-500/30';
            default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
        }
    };

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 text-blue-500 mb-6">
                        <Bell size={32} />
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Latest Announcements</h1>
                    <p className="text-gray-400 max-w-xl mx-auto">
                        Stay updated with the latest news, listings, and system updates from Albatech.
                    </p>
                </div>

                <div className="space-y-6">
                    {loading ? (
                        // Skeleton loading
                        [1, 2, 3].map(i => (
                            <div key={i} className="bg-[#1e2329] rounded-2xl p-6 border border-gray-800 animate-pulse">
                                <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
                                <div className="h-4 bg-gray-800 rounded w-full mb-2"></div>
                                <div className="h-4 bg-gray-800 rounded w-1/2"></div>
                            </div>
                        ))
                    ) : announcements.length > 0 ? (
                        announcements.map(item => (
                            <div key={item.id} className="bg-[#1e2329] hover:bg-[#2b3139] border border-gray-800 rounded-2xl p-6 sm:p-8 transition-all hover:shadow-xl hover:-translate-y-1 group">
                                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className={`px-2 py-0.5 rounded text-xs border ${getBadgeColor(item.type)} uppercase font-bold tracking-wider`}>
                                                {item.type || 'Info'}
                                            </span>
                                            <span className="text-gray-500 text-sm flex items-center gap-1">
                                                <Calendar size={14} />
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition">{item.title}</h3>
                                    </div>
                                </div>

                                <p className="text-gray-400 leading-relaxed mb-4">
                                    {item.content}
                                </p>

                                {/* Optional: Add 'Read More' if content is long, currently showing all */}
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-16 bg-[#1e2329] rounded-2xl border border-gray-800">
                            <Bell size={48} className="mx-auto text-gray-600 mb-4" />
                            <p className="text-gray-400">No announcements at this time.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnouncementsPage;
